import React, { useEffect, useState } from 'react';
import {
  View, Text, StyleSheet, ActivityIndicator, Pressable,
} from 'react-native';
import Modal from 'react-native-modal';
import { useSelector } from 'react-redux';
import editSpace from '../../../../../../requests/Locations/Spaces/EditSpace';
import deleteSpace from '../../../../../../requests/Locations/Spaces/DeleteSpace';
import {
  DELETE_SPACE_FAILURE, EDIT_SPACE_FAILURE, PROVIDE_CAPACITY,
} from '../../../../../../constants/AlertMessages';
import { LocationSubtypeTitles, Space } from '../../../../../../components/Map/SetFloorsModal/FloorItem';
import asNumber from '../../../../../../components/AsNumber';
import nameRegex, { externalIdRegex, integerRegex } from '../../../../../../components/Regex';
import Style from '../../../../../../constants/Style';
import PressableStyled from '../../../../../../components/PressableStyled';
import CloseModalIcon from '../../../../../../components/CloseModalIcon';
import CustomTextField from '../../../../../../components/CustomTextField';
import PriorityIndicator from '../../../PriorityIndicator';
import BinIcon24 from '../../../../../../components/24pxIcons/BinIcon24';
import Button from '../../../../../../components/Button';
import AlertModal from '../../../../../../components/AlertModal';
import Colors from '../../../../../../constants/Colors';
import { UserPermission } from '../../../../../../constants/Types';

interface EditSpaceModalProps {
  space: Space,
  // eslint-disable-next-line no-unused-vars
  updateSpace: (space: Space) => void,
  removeSpace: Function,
  editSpaceModalShown: boolean,
  hideEditSpaceModal: () => void,
  showScreenAlert,
  parentName: string,
}

const EditSpaceModal = ({
  space, updateSpace, editSpaceModalShown, hideEditSpaceModal, showScreenAlert, removeSpace,
  parentName,
}: EditSpaceModalProps) => {
  if (!space) return null;

  const { isAdmin, permissions }: { isAdmin: boolean, permissions: UserPermission[] } = useSelector(
    (state: { user: { isAdmin: boolean, permissions: UserPermission[] } }) => state.user,
  );
  const canDeleteLocations: boolean = isAdmin
    || permissions.includes(UserPermission.LOCATIONS_DELETE);
  const [loading, setLoading] = useState<boolean>(false);
  const [name, setName] = useState<string>(space.Name?.trim());
  const [capacity, setCapacity] = useState<number | null>(asNumber(space.LocationCapacity));
  const [externalId, setExternalId] = useState<string | null>(space.ExternalId?.trim());
  const [isHighPriority, setIsHighPriority] = useState<boolean>(space.IsHighPriority === true);
  const [priorityDropdownShown, setPriorityDropdownShown] = useState<boolean>(false);
  const [capacityError, setCapacityError] = useState<string | null>(null);
  const [alertTitle, setAlertTitle] = useState<string>('Error');
  const [alertMessage, setAlertMessage] = useState<string>('');
  const [alertError, setAlertError] = useState<boolean>(true);
  const [alertAction, setAlertAction] = useState<() => void>(null);
  const [alertActionTitle, setAlertActionTitle] = useState<string>('');
  const [alertVisible, setAlertVisible] = useState<boolean>(false);
  const [nameInvalidMessage, setNameInvalidMessage] = useState<string | null>(null);
  const [externalIdInvalidMessage, setExternalIdInvalidMessage] = useState<string | null>(null);

  const disabled: boolean = !!nameInvalidMessage || !!externalIdInvalidMessage;

  useEffect(() => {
    if (!(name?.length > 0)) setNameInvalidMessage('Name cannot be empty');
    else if (name?.length > 50) setNameInvalidMessage('Name cannot exceed 50 characters');
    else if (!nameRegex.exp.test(name)) {
      setNameInvalidMessage(`Name${nameRegex.message}`);
    } else setNameInvalidMessage(null);
    if (externalId?.trim().length > 0 && !externalIdRegex.exp.test(externalId.trim())) {
      setExternalIdInvalidMessage(externalIdRegex.message);
    } else setExternalIdInvalidMessage(null);
  }, [name, externalId]);

  const updateCapacity = (text: string) => {
    const value: number | null = asNumber(text);
    if (text?.trim().length > 0 && !integerRegex.exp.test(text.trim())) {
      setCapacityError(`Capacity${integerRegex.message}`);
    } else if (space.LocationSubtype && !value) {
      setCapacityError(PROVIDE_CAPACITY);
    } else setCapacityError(null);
    setCapacity(value);
  };

  const showAlert = (
    title: string,
    message: string,
    error: boolean = true,
    action: () => void = null,
    actionTitle: string = '',
  ) => {
    setAlertTitle(title);
    setAlertMessage(message);
    setAlertError(error);
    setAlertAction(() => action);
    setAlertActionTitle(actionTitle);
    setAlertVisible(true);
  };

  const saveError = (errorText?: string) => {
    setLoading(false);
    showAlert('Error', errorText ?? EDIT_SPACE_FAILURE);
  };

  const deleteError = (errorText?: string) => {
    setLoading(false);
    showAlert('Error', errorText ?? DELETE_SPACE_FAILURE);
  };

  const save: () => void = () => {
    if (disabled) return;
    setCapacityError(null);
    setLoading(true);
    editSpace({
      LocationId: space.LocationId,
      Space: {
        Name: name,
        Top: space.Top,
        Left: space.Left,
        Right: space.Right,
        Bottom: space.Bottom,
        Points: space.Points,
        Polygon: space.Polygon,
        Priority: isHighPriority === true,
        ParentId: space.ParentId,
        ExternalId: externalId,
        LocationSubtype: space.LocationSubtype,
        LocationCapacity: capacity,
      },
    }).then((res) => {
      if (res && !res.isError) {
        updateSpace({
          ...space,
          Name: name,
          IsHighPriority: isHighPriority,
          ExternalId: externalId,
          LocationCapacity: capacity,
        });
        setLoading(false);
        hideEditSpaceModal();
        showScreenAlert(
          `Updated ${name}`,
          <Text>
            {'Name and priority of the space '}
            <Text style={Style.alertBoldText}>{name}</Text>
            {' was successfully updated'}
          </Text>,
          false,
        );
      } else saveError(res?.errorText);
    }).catch((error) => saveError(error?.message));
  };

  const remove: () => void = () => showAlert(
    `Delete ${space.Name}?`,
    'Are you sure you wish to delete this space?',
    true,
    () => {
      setLoading(true);
      deleteSpace(space.LocationId).then((res) => {
        if (res && !res.isError) {
          removeSpace(space.LocationId);
          setLoading(false);
          hideEditSpaceModal();
          showScreenAlert(
            `Deleted ${space.Name}`,
            <Text>
              <Text style={Style.alertBoldText}>{space.Name}</Text>
              {' was successfully deleted from '}
              <Text style={Style.alertBoldText}>{parentName}</Text>
            </Text>,
            false,
          );
        } else deleteError(res?.errorText);
      }).catch((error) => deleteError(error?.message));
    },
    'Delete',
  );

  const onModalHide: () => void = () => {
    setName(space.Name?.trim());
    setNameInvalidMessage(null);
    setCapacity(asNumber(space.LocationCapacity));
    setCapacityError(null);
    setExternalId(space.ExternalId?.trim());
    setExternalIdInvalidMessage(null);
    setIsHighPriority(space.IsHighPriority === true);
    setLoading(false);
  };

  return <Modal
    isVisible={editSpaceModalShown}
    onBackButtonPress={hideEditSpaceModal}
    onBackdropPress={hideEditSpaceModal}
    onSwipeComplete={hideEditSpaceModal}
    {...{ onModalHide }}
    onModalWillShow={onModalHide}
    style={Style.modal}
  >
    <Pressable onPress={() => setPriorityDropdownShown(false)}>
      <View style={[Style.modalContent, styles.container]}>
        <PressableStyled
          style={Style.closeButton}
          onPress={hideEditSpaceModal}>
          <CloseModalIcon />
        </PressableStyled>
        <Text style={styles.title}>{'Edit space'}</Text>
        <Text style={[styles.label, styles.subTitle]}>{'Fields marked with * are mandatory'}</Text>
        <View style={styles.textFieldContainer}>
          <CustomTextField
            required
            title='Name'
            value={name}
            maxLength={50}
            updateValue={setName}
            placeholder='Enter space name'
            invalid={!!nameInvalidMessage}
            errorText={nameInvalidMessage}
          />
          <PriorityIndicator
            style={styles.priorityIndicator}
            {...{
              priorityDropdownShown, setPriorityDropdownShown, isHighPriority, setIsHighPriority,
            }}
          />
        </View>
        {space.LocationSubtype?.length > 0
          && LocationSubtypeTitles[space.LocationSubtype]?.length > 0
          && <>
            <Text style={styles.label}>{'Location type *'}</Text>
            <View style={styles.locationType}>
              <Text style={styles.locationSubtypeText}>
                {LocationSubtypeTitles[space.LocationSubtype]}
              </Text>
            </View>
          </>}
        {!!space.LocationSubtype && <CustomTextField
          title='Capacity'
          maxLength={3}
          invalid={!!capacityError}
          errorText={capacityError}
          value={capacity?.toString()}
          updateValue={updateCapacity}
          placeholder='Enter capacity'
        />}
        <CustomTextField
          title='External ID'
          value={externalId}
          maxLength={50}
          updateValue={setExternalId}
          placeholder='Enter external ID'
          invalid={!!externalIdInvalidMessage}
          errorText={externalIdInvalidMessage}
        />
        <View style={[Style.row, styles.buttonContainer]}>
          {canDeleteLocations ? <PressableStyled
            style={styles.deleteButton}
            onPress={remove}
            testID='edit-modal-delete-btn'
          >
            <BinIcon24 color={styles.deleteButton.borderColor} />
          </PressableStyled> : <View />}
          <View style={Style.row}>
            <Button
              title='Cancel'
              onPress={hideEditSpaceModal}
              testID='edit-modal-cancel-btn'
            />
            <Button
              affirmative
              title='Save'
              {...{ disabled }}
              onPress={save}
              testID='edit-modal-save-btn'
            />
          </View>
        </View>
        <ActivityIndicator
          size='large'
          style={Style.activityIndicator}
          animating={loading}
        />
      </View>
    </Pressable>
    <AlertModal
      title={alertTitle}
      message={alertMessage}
      error={alertError}
      action={alertAction}
      actionTitle={alertActionTitle}
      modalVisible={alertVisible}
      setModalVisible={setAlertVisible}
    />
  </Modal>;
};

const styles = StyleSheet.create({
  container: {
    padding: 40,
    paddingTop: 60,
    alignSelf: 'center',
    width: 532,
  },
  title: {
    fontSize: 28,
    lineHeight: 35,
    fontFamily: 'Poppins_600SemiBold',
    color: Colors.light.hereworksBlack600,
    alignSelf: 'center',
    textAlign: 'center',
    marginBottom: 12,
  },
  label: {
    fontSize: 16,
    lineHeight: 24,
    fontFamily: 'Roboto_400Regular',
    color: Colors.light.hereworksBlack400,
    marginBottom: 4,
  },
  subTitle: {
    marginBottom: 28,
    alignSelf: 'center',
    textAlign: 'center',
  },
  textFieldContainer: {
    zIndex: 2,
    marginBottom: 12,
  },
  priorityIndicator: {
    position: 'absolute',
    end: 10,
    top: 50,
  },
  buttonContainer: {
    marginTop: 40,
  },
  deleteButton: {
    borderWidth: 1,
    borderRadius: 4,
    borderColor: Colors.light.hereworksRed700,
    justifyContent: 'center',
    alignItems: 'center',
    width: 40,
    height: 40,
  },
  locationType: {
    borderRadius: 10,
    borderWidth: 1.5,
    borderColor: Colors.light.hereworksBlue100,
    backgroundColor: '#FFF',
    padding: 12,
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'space-between',
  },
  locationSubtypeText: {
    fontSize: 14,
    lineHeight: 21,
    fontFamily: 'Poppins_400Regular',
    color: Colors.light.hereworksBlack600,
  },
});

export default EditSpaceModal;
